/**
 * SpaceSyncMultiplexer
 *
 * Input:  WebSocket URI for /spaces/sync/:spaceId
 * Output: virtual TLPersistentClientSocket channels keyed by roomId
 * Mutates: manages one real WebSocket; each channel maintains its own status
 *
 * Multiplexes multiple TLSync channels over a single WebSocket using
 * SyncEnvelope framing: { roomId, payload }. Each channel behaves as an
 * independent TLPersistentClientSocket for use with useSync({ connect }).
 */

import type {
  TLPersistentClientSocket,
  TLSocketStatusChangeEvent,
} from "@tldraw/sync";

/**
 * Reserved channel name for the space-wide shared room (slides, teacher's
 * shared canvas). Any other channel name addresses a user's personal room
 * (keyed by their userId). Must match the gateway's roomId routing in
 * Space.ts.
 */
export const SHARED_CHANNEL = "shared";

type SyncEnvelope = { roomId: string; payload: object };

// Reconnect backoff: doubles each failed attempt up to MAX_RETRY_DELAY_MS,
// starting at INITIAL_RETRY_DELAY_MS. Reset to 0 attempts on every successful
// onopen.
const INITIAL_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 30_000;

// ---------------------------------------------------------------------------
// SyncChannel — one virtual TLPersistentClientSocket per roomId
// ---------------------------------------------------------------------------

class SyncChannel implements TLPersistentClientSocket {
  connectionStatus: "online" | "offline" | "error" = "offline";
  private _messageCallbacks = new Set<(msg: object) => void>();
  private _statusCallbacks = new Set<
    (evt: TLSocketStatusChangeEvent) => void
  >();

  constructor(
    private readonly _roomId: string,
    private readonly _mux: SpaceSyncMultiplexer,
  ) {}

  sendMessage(msg: object): void {
    this._mux._send(this._roomId, msg);
  }

  onReceiveMessage(cb: (msg: object) => void): () => void {
    this._messageCallbacks.add(cb);
    return () => this._messageCallbacks.delete(cb);
  }

  onStatusChange(cb: (evt: TLSocketStatusChangeEvent) => void): () => void {
    this._statusCallbacks.add(cb);
    return () => this._statusCallbacks.delete(cb);
  }

  /**
   * Called by tldraw's useSync when this channel needs to recycle its session
   * (e.g. after a sync error). Two paths:
   *   • WS is OPEN — only THIS channel is bounced (offline → online). Other
   *     channels and the underlying WS keep running. Generation is NOT bumped.
   *   • WS is broken — falls through to the multiplexer's _reconnect, which
   *     tears down the WS, bumps generation, and reconnects. Other channels
   *     are forced offline alongside.
   */
  restart(): void {
    const wsState = this._mux._wsReadyState();
    if (wsState === WebSocket.OPEN) {
      this._onStatusChange({ status: "offline" });
      this._onStatusChange({ status: "online" });
    } else {
      this._mux._reconnect();
    }
  }

  close(): void {
    // Signal DO to drop its watching session for this room so the next connect
    // gets a fresh handshake and a full initial snapshot rather than resuming a
    // stale session. Only send for personal rooms — the server ignores it on the
    // shared channel and future expansion of the handler shouldn't treat a
    // spurious shared-room close as anything meaningful.
    const isPersonal = this._roomId !== SHARED_CHANNEL;
    if (isPersonal && this._mux._wsReadyState() === WebSocket.OPEN) {
      this._mux._send(this._roomId, { __mux_close__: true });
    }
    this._mux._closeChannel(this._roomId);
  }

  // Called by mux
  _onMessage(payload: object): void {
    for (const cb of this._messageCallbacks) cb(payload);
  }

  _onStatusChange(evt: TLSocketStatusChangeEvent): void {
    this.connectionStatus =
      evt.status === "online"
        ? "online"
        : evt.status === "offline"
          ? "offline"
          : "error";
    for (const cb of this._statusCallbacks) cb(evt);
  }
}

// ---------------------------------------------------------------------------
// SpaceSyncMultiplexer
// ---------------------------------------------------------------------------

export class SpaceSyncMultiplexer {
  private _ws: WebSocket | null = null;
  private _uri: string;
  private _channels = new Map<string, SyncChannel>();
  private _closed = false;
  private _connectionListeners = new Set<(isConnected: boolean) => void>();
  private _isLastConnected: boolean | null = null;
  // Bumped after every unexpected disconnect (DO restart, network drop, …).
  // Consumers use this as a React `key` so useSync fully remounts on reconnect —
  // a fresh clientId + clean handshake — instead of trying to resume a session
  // the server no longer knows about.
  private _generation = 0;
  private _generationListeners = new Set<(generation: number) => void>();

  constructor(uri: string) {
    this._uri = uri;
    this._connect();
  }

  /**
   * Subscribe to underlying WebSocket connection state.
   * Fires immediately with current state if known, then on every transition.
   */
  onConnectionChange(cb: (isConnected: boolean) => void): () => void {
    this._connectionListeners.add(cb);
    if (this._isLastConnected !== null) cb(this._isLastConnected);
    return () => this._connectionListeners.delete(cb);
  }

  private _emitConnection(isConnected: boolean): void {
    if (this._isLastConnected === isConnected) return;
    this._isLastConnected = isConnected;
    for (const cb of this._connectionListeners) cb(isConnected);
  }

  /**
   * Subscribe to generation bumps. Fires immediately with current generation,
   * then again every time the underlying socket reconnects after a disconnect.
   */
  onGenerationChange(cb: (generation: number) => void): () => void {
    this._generationListeners.add(cb);
    cb(this._generation);
    return () => this._generationListeners.delete(cb);
  }

  get generation(): number {
    return this._generation;
  }

  private _bumpGeneration(): void {
    this._generation++;
    // Drop all existing virtual channels — any consumer subscribed to them is
    // about to unmount via the keyed parent. Old SyncChannel instances must
    // not receive further messages from a freshly handshaken socket.
    this._channels.clear();
    for (const cb of this._generationListeners) cb(this._generation);
  }

  /**
   * Input:  roomId (SHARED_CHANNEL | userId)
   * Output: TLPersistentClientSocket for that room — created on first call
   */
  getChannel(roomId: string): TLPersistentClientSocket {
    let channel = this._channels.get(roomId);
    if (!channel) {
      channel = new SyncChannel(roomId, this);
      this._channels.set(roomId, channel);
      // If WS already open, immediately notify this channel
      if (this._ws?.readyState === WebSocket.OPEN) {
        channel._onStatusChange({ status: "online" });
      }
    }
    return channel;
  }

  close(): void {
    this._closed = true;
    if (this._retryTimer !== null) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
    this._ws?.close();
    this._ws = null;
    this._channels.clear();
    this._connectionListeners.clear();
    this._generationListeners.clear();
  }

  // ---------------------------------------------------------------------------
  // Internal — called by SyncChannel
  // ---------------------------------------------------------------------------

  _send(roomId: string, payload: object): void {
    if (this._ws?.readyState === WebSocket.OPEN) {
      const envelope: SyncEnvelope = { roomId, payload };
      this._ws.send(JSON.stringify(envelope));
    }
  }

  _wsReadyState(): number {
    return this._ws?.readyState ?? WebSocket.CLOSED;
  }

  _reconnect(): void {
    if (this._closed) return;
    // Do NOT reset _retryCount here — a channel-requested restart means the WS
    // dropped unexpectedly. Preserving the count keeps the backoff growing so a
    // repeatedly-crashing server doesn't get hammered every second.
    if (this._retryTimer !== null) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
    this._ws?.close();
    this._ws = null;
    this._channels.forEach((ch) => ch._onStatusChange({ status: "offline" }));
    this._bumpGeneration();
    this._connect();
  }

  _closeChannel(roomId: string): void {
    this._channels.delete(roomId);
  }

  // ---------------------------------------------------------------------------
  // WebSocket lifecycle
  // ---------------------------------------------------------------------------

  // Backoff state for auto-reconnect on unexpected close.
  private _retryCount = 0;
  private _retryTimer: ReturnType<typeof setTimeout> | null = null;

  private _connect(): void {
    if (this._closed) return;
    if (this._retryTimer !== null) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
    const ws = new WebSocket(this._uri);
    this._ws = ws;

    ws.onopen = () => {
      if (ws !== this._ws) return;
      this._retryCount = 0;
      this._emitConnection(true);
      this._channels.forEach((ch) => ch._onStatusChange({ status: "online" }));
    };

    ws.onmessage = (evt) => {
      if (ws !== this._ws) return;
      let envelope: SyncEnvelope;
      try {
        envelope = JSON.parse(evt.data as string) as SyncEnvelope;
      } catch {
        return;
      }
      this._channels.get(envelope.roomId)?._onMessage(envelope.payload);
    };

    ws.onclose = () => {
      if (ws !== this._ws) return;
      this._ws = null;
      this._emitConnection(false);
      this._channels.forEach((ch) => ch._onStatusChange({ status: "offline" }));
      // Bump generation BEFORE scheduling the reconnect so consumers unmount
      // their useSync subscriptions and the next connect starts with a fresh
      // tldraw client (clean handshake against a possibly-restarted DO).
      if (!this._closed) {
        this._bumpGeneration();
        const delay_ms = Math.min(
          INITIAL_RETRY_DELAY_MS * Math.pow(2, this._retryCount),
          MAX_RETRY_DELAY_MS,
        );
        this._retryCount++;
        this._retryTimer = setTimeout(() => this._connect(), delay_ms);
      }
    };

    ws.onerror = () => {
      if (ws !== this._ws) return;
      this._channels.forEach((ch) =>
        ch._onStatusChange({ status: "error", reason: "WebSocket error" }),
      );
    };
  }
}
